import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createFollowUp, getUsers, getClients, getLeads, getClientMappedEmployees } from '../../lib/api';
import { formatApiError, getClientDisplayName } from '../../lib/utils';
import { FOLLOW_UP_TYPES, PRIORITIES, FOLLOWUP_TIME_SLOTS } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { DatePicker } from '../../components/ui/date-picker';
import { TimePicker } from '../../components/ui/time-picker';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft } from '@phosphor-icons/react';

export default function FollowUpFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forType = searchParams.get('for') || 'client'; // 'client' or 'lead'

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [targetType, setTargetType] = useState(forType);
  const [mappedEmployees, setMappedEmployees] = useState([]);

  const [formData, setFormData] = useState({
    client_id: '',
    lead_id: '',
    follow_up_type: '',
    due_date: '',
    due_time: '10:00',
    priority: 'Medium',
    description: '',
    assigned_to: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, clientsRes, leadsRes] = await Promise.all([
          getUsers(), getClients(), getLeads()
        ]);
        setUsers(usersRes.data);
        setClients(clientsRes.data);
        setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data || []);
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch mapped employees when client is selected
  useEffect(() => {
    if (targetType === 'client' && formData.client_id) {
      getClientMappedEmployees(formData.client_id).then(res => setMappedEmployees(res.data)).catch(() => setMappedEmployees([]));
    } else {
      setMappedEmployees([]);
    }
  }, [targetType, formData.client_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (targetType === 'client' && !formData.client_id) { toast.error('Please select a client'); return; }
    if (targetType === 'lead' && !formData.lead_id) { toast.error('Please select a lead'); return; }
    if (!formData.follow_up_type) { toast.error('Please select a type'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        client_id: targetType === 'client' ? formData.client_id : undefined,
        lead_id: targetType === 'lead' ? formData.lead_id : undefined,
      };
      await createFollowUp(payload);
      toast.success('Follow-up created successfully');
      navigate('/follow-ups');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="followup-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/follow-ups')} className="p-2">
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          New Follow-up
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl">
        <Card className="border-zinc-200 rounded-sm mb-6">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Follow-up For</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant={targetType === 'client' ? 'default' : 'outline'}
                className={targetType === 'client' ? 'bg-[#002FA7] text-white rounded-sm' : 'rounded-sm'}
                onClick={() => { setTargetType('client'); setFormData(p => ({ ...p, lead_id: '' })); }}
                data-testid="target-client-btn"
              >
                Client
              </Button>
              <Button
                type="button"
                variant={targetType === 'lead' ? 'default' : 'outline'}
                className={targetType === 'lead' ? 'bg-amber-600 text-white rounded-sm' : 'rounded-sm'}
                onClick={() => { setTargetType('lead'); setFormData(p => ({ ...p, client_id: '' })); }}
                data-testid="target-lead-btn"
              >
                Lead
              </Button>
            </div>

            {targetType === 'client' ? (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                  Client <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.client_id}
                  onValueChange={(v) => setFormData(p => ({ ...p, client_id: v }))}
                  options={clients.map(c => ({ value: c.id, label: getClientDisplayName(c) }))}
                  placeholder="Select client"
                  searchPlaceholder="Search clients..."
                  data-testid="followup-client-select"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                  Lead <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.lead_id}
                  onValueChange={(v) => setFormData(p => ({ ...p, lead_id: v }))}
                  options={leads.map(l => ({ value: l.id, label: `${l.full_name || l.name || 'Unnamed'} ${l.phone ? `(${l.phone})` : ''}` }))}
                  placeholder="Select lead"
                  searchPlaceholder="Search leads..."
                  data-testid="followup-lead-select"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-sm">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Follow-up Details</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                  Type <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.follow_up_type}
                  onValueChange={(v) => setFormData(p => ({ ...p, follow_up_type: v }))}
                  options={FOLLOW_UP_TYPES}
                  placeholder="Select type"
                  data-testid="followup-type-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                  Priority <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.priority}
                  onValueChange={(v) => setFormData(p => ({ ...p, priority: v }))}
                  options={PRIORITIES}
                  placeholder="Select priority"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                  Due Date <span className="text-red-500">*</span>
                </Label>
                <DatePicker
                  value={formData.due_date}
                  onChange={(v) => setFormData(p => ({ ...p, due_date: v }))}
                  placeholder="Select due date"
                  data-testid="followup-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                  Due Time <span className="text-red-500">*</span>
                </Label>
                <TimePicker
                  value={formData.due_time}
                  onChange={(v) => setFormData(p => ({ ...p, due_time: v }))}
                  slots={FOLLOWUP_TIME_SLOTS}
                  placeholder="Select time"
                  data-testid="due-time-select"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                Assigned To
              </Label>
              <SearchableSelect
                value={formData.assigned_to}
                onValueChange={(v) => setFormData(p => ({ ...p, assigned_to: v }))}
                options={(targetType === 'client' && mappedEmployees.length > 0 ? mappedEmployees : users.filter(u => u.status === 'active' || u.id)).map(u => ({ value: u.id, label: u.name }))}
                placeholder="Select employee"
                searchPlaceholder="Search employees..."
                data-testid="followup-assigned-to"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                Description
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                rows={3}
                className="rounded-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-100">
              <Button type="button" variant="outline" onClick={() => navigate('/follow-ups')} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="followup-submit-button">
                {saving ? 'Creating...' : 'Create Follow-up'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
